import { useEffect, useRef } from 'react';
import CloseIcon from '~/icons/close.svg';
import { DynamicImageLoader } from '~/shared/components/DynamicImageLoader';
import styles from './ProductInfoLabelDialog.module.scss';
import { usePDPState } from '~/features/product-details/context';

interface Props {
    /** Must be stable, since it takes part in the Escape listener's effect. */
    onClose: () => void;
}

/**
 * The overlay that shows a product label at full size on top of the gallery. One instance
 * serves every label, since only one can be open at a time.
 */
export const ProductInfoLabelDialog: React.FC<Props> = ({ onClose }) => {
    const closeRef = useRef<HTMLButtonElement>(null);
    const { infoLabelDialog } = usePDPState();

    useEffect(() => {
        if (!infoLabelDialog) return;

        closeRef.current?.focus();

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };

        window.addEventListener('keydown', onKeyDown);

        return () => window.removeEventListener('keydown', onKeyDown);
    }, [infoLabelDialog, onClose]);

    if (!infoLabelDialog) return null;

    const { src, alt, label, closeLabel } = infoLabelDialog;

    return (
        <div
            className={styles.productInfoLabelDialog}
            role="dialog"
            aria-label={label}
            onClick={onClose}
        >
            {/* Clicking beside the label closes it, so clicks on the label must not bubble. */}
            <div
                className={styles.productInfoLabelDialogContent}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    ref={closeRef}
                    className={styles.productInfoLabelDialogClose}
                    onClick={onClose}
                    aria-label={closeLabel}
                >
                    <CloseIcon aria-hidden="true" focusable="false" />
                </button>

                <DynamicImageLoader width={500} height={500} src={src} alt={alt ?? label} />
            </div>
        </div>
    );
};
